/**
 * "Get ready for your opening inspection" (owner, 2026-10-03, from Carson City HHS's pre-opening page): an ordered checklist per
 * health authority, inherited like every rule (jurisdiction.ts keys "pre_opening.<item>"). The baseline is the FDA Food Code
 * model, labeled guidance; an authority extends it or replaces an item with its own expectation (labeled as that authority's,
 * and as stricter where it goes past the rule here). Each item names the app feature that proves it (`proof`), so one tap opens
 * the thermometer calibration log, the sanitizer test, the ice machine guide, the hand sink signs, role coverage or the
 * employee health policy. "Ready for the inspector" fills the binder's pre-opening page.
 *
 * Also here: when the first inspection is likely, and how often after (the authority's `inspection.frequency`), for planning
 * reminders. Data: states/preopening-data.ts.
 */
import type { IsoDate } from "./model.ts";
import { chainFor, resolveIn, type Place, type RuleValue, type World } from "./jurisdiction.ts";

/** What proves an item in the app (the screens map each kind to its route). */
export type PreOpeningProof =
  | { kind: "log"; templateId: string; buy?: string }
  | { kind: "buy_then_log"; supplyKey: string; templateId: string }
  | { kind: "guide"; guideId: string }
  | { kind: "signs" }
  | { kind: "checklist" }
  | { kind: "equipment"; objectType: string }
  | { kind: "roles" }
  | { kind: "health_policy" }
  | { kind: "none" };

export interface PreOpeningItem {
  id: string;
  order: number;
  text: string;
  /** Food Code sections (baseline) or the authority's own words. */
  section?: string;
  proof: PreOpeningProof;
  /** The authority's own expectation (not the FDA baseline). */
  local?: boolean;
  /** Where it goes past the rule here, in plain words ("0°F freezers isn't in the Food Code"). */
  stricter?: string;
}

export interface PreOpeningLine extends PreOpeningItem {
  /** Whose item it is: "FDA Food Code (guidance)" or the authority's name. */
  from: string;
  citation: string;
  status: RuleValue["status"];
}

/** The pre-opening checklist for a place: the baseline, then its authority's items (replacing a baseline item by id), in order. */
export function preOpeningChecklist(world: World, place: Place, o: { date?: IsoDate } = {}): PreOpeningLine[] {
  const ids = chainFor(world, place).map((n) => n.id);
  const keys = [...new Set(world.rules.filter((r) => r.key.startsWith("pre_opening.") && ids.includes(r.node)).map((r) => r.key))];
  const out: PreOpeningLine[] = [];
  for (const k of keys) {
    const r = resolveIn<PreOpeningItem>(world, k, place, o);
    if (!r.rule || !r.value || r.status === "unknown") continue;
    const node = world.nodes.find((n) => n.id === r.rule!.node);
    out.push({ ...r.value, from: node?.level === "country" ? "FDA Food Code (guidance)" : node?.name ?? r.rule.node, citation: r.rule.source?.citation ?? "", status: r.status });
  }
  return out.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export interface InspectionFrequency { firstWithinDays?: number; cadence?: { risk: string; perYear: number }[] }

/** "Your first inspection is likely within a month of opening …", or null where the authority's cadence isn't known. */
export function inspectionPlanLine(world: World, place: Place, o: { date?: IsoDate } = {}): { line: string; citation: string } | null {
  const r = resolveIn<InspectionFrequency>(world, "inspection.frequency", place, o);
  const f = r.value;
  if (!f || r.status === "unknown") return null;
  const first = f.firstWithinDays ? `Your first inspection is likely within ${f.firstWithinDays <= 31 ? "a month" : `${f.firstWithinDays} days`} of opening, unannounced.` : "";
  const cad = f.cadence?.length ? ` Then at least ${f.cadence.map((c) => `${c.perYear === 1 ? "once" : c.perYear === 2 ? "twice" : `${c.perYear} times`} a year for ${c.risk}`).join(", and ")}.` : "";
  return { line: `${first}${cad}`.trim(), citation: r.rule?.source?.citation ?? "" };
}

/** The opening-gate step id, and each item's progress id ("pre-opening:<item>"). */
export const PRE_OPENING_STEP_ID = "opening-inspection-ready";
export const preOpeningItemStepId = (itemId: string) => `pre-opening:${itemId}`;
export const isPreOpeningItemStepId = (id: string) => /^pre-opening:[a-z0-9-]{2,60}$/.test(id);

/**
 * The binder's "Pre-opening inspection" page: each checklist item, whose it is, and whether it was marked ready, plus when
 * "Ready for the inspector" was marked and by whom. A record of what a person marked, not an inspection.
 */
export function preOpeningSection(p: { items: readonly (PreOpeningLine & { done: boolean })[]; readyAt?: string; readyBy?: string }, timeLabel: (iso: string) => string): import("./binder.ts").BinderSection {
  return {
    id: "pre-opening",
    heading: "Pre-opening inspection",
    intro: `What the inspector looks at before the permit, item by item, and what was marked ready. ${p.readyAt ? `"Ready for the inspector" marked ${timeLabel(p.readyAt)}${p.readyBy ? ` by ${p.readyBy}` : ""}.` : "Not yet marked ready for the inspector."} A mark says someone checked it; it isn't an inspection.`,
    table: { columns: ["Item", "Whose list", "Marked"], widths: [4, 2, 1.2], rows: p.items.map((x) => ({ cells: [x.text + (x.stricter ? ` (${x.stricter})` : ""), x.from, x.done ? "Ready" : "Not marked"] })) },
  };
}
